import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { shippingApi } from '../api/shippingApi';
import {
  INBOUND_PRINT_COL_WIDTHS,
  PRINT_PAGE_TITLE_COUNT,
  PRINT_PAGE_TITLE_H1,
  PRINT_PAGE_TITLE_ROW,
  PRINT_SCROLL_CONTAINER,
  PRINT_SEARCH_ROW_TH,
  PRINT_SEARCH_ROW_TR,
  PRINT_TABLE,
  PRINT_TD_COMPOSITE,
  PRINT_TD_LEFT,
  PRINT_TD_NUM,
  PRINT_THEAD,
  PRINT_TH_LEFT,
  PRINT_TH_RIGHT,
  PRINT_TR_BODY,
} from '../constants/printTableStyles';
import { PageSyncMeta } from '../components/sync/PageSyncMeta';
import { useSyncStatus } from '../contexts/SyncStatusContext';

const InboundSchedulePage = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('tomorrow'); // デフォルトは「明日」
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const { refreshSyncStatus } = useSyncStatus();
  const isPrintView = searchParams.get('print_view') === '1';
  const PRINT_MAX_COUNT = 1000;
  const PRINT_WARNING_THRESHOLD = 200;

  const tabs = [
    { id: 'tomorrow', label: '明日' },
    { id: 'after_tomorrow', label: '明日以降' },
    { id: 'today', label: '今日' },
  ];

  const loadItems = async (dateFilter) => {
    try {
      setLoading(true);
      setLoadError(null);
      const response = await shippingApi.getInboundSchedule(dateFilter);
      const data = response.data || response;
      setItems(data);
    } catch (error) {
      console.error('入荷予定一覧取得エラー:', error);
      setLoadError('入荷予定一覧の取得に失敗しました');
      alert('入荷予定一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isPrintView) {
      document.body.classList.add('shipping-print-view');
    }
    return () => {
      document.body.classList.remove('shipping-print-view');
    };
  }, [isPrintView]);

  useEffect(() => {
    loadItems(activeTab);
  }, [activeTab]);

  const handleRefresh = async () => {
    await Promise.all([loadItems(activeTab), refreshSyncStatus()]);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '-';
    return num.toLocaleString();
  };

  const getTabLabel = () => {
    const map = { today: '今日', tomorrow: '明日', after_tomorrow: '明日以降' };
    return map[activeTab] || activeTab;
  };

  const getPrintSearchSummary = () => {
    return `検索条件: タブ=${getTabLabel()}`;
  };

  const renderPrintView = () => {
    // 分納表示行（is_split_plan）は印刷対象外
    const printSource = items.filter((row) => !row.is_split_plan);
    const printCount = Math.min(printSource.length, PRINT_MAX_COUNT);

    return (
      <div className="h-full flex flex-col bg-white print-view-root">
        <div className="sticky top-0 z-20 page-section print:hidden">
          <div className="page-header">
            <div>
              <h1 className="text-xl font-semibold text-slate-900 compact:text-lg">入荷予定（印刷用）</h1>
              <p className="meta-text mt-1">{getPrintSearchSummary()}</p>
              {loadError && <p className="text-xs text-red-600 mt-1">{loadError}</p>}
            </div>
            <div className="page-header-actions">
              <button
                type="button"
                onClick={() => {
                  if (window.opener && window.history.length <= 1) {
                    window.close();
                    return;
                  }
                  const url = new URL(window.location.href);
                  url.searchParams.delete('print_view');
                  window.location.href = url.toString();
                }}
                className="btn-toolbar text-sm border border-slate-300 rounded-md text-slate-700 bg-white hover:bg-slate-100"
              >
                戻る
              </button>
              <button
                type="button"
                onClick={() => loadItems(activeTab)}
                disabled={loading}
                className="btn-toolbar text-sm border border-slate-300 rounded-md text-slate-700 bg-white hover:bg-slate-100 disabled:opacity-50"
              >
                再試行
              </button>
              <div className="meta-text">
                件数: {printCount}
              </div>
              <button
                type="button"
                onClick={() => {
                  if (printCount > PRINT_MAX_COUNT) {
                    alert(`印刷上限の${PRINT_MAX_COUNT}件を超えています。条件を絞り込んでください。`);
                    return;
                  }
                  if (printCount === 0) {
                    alert('印刷対象データがありません。');
                    return;
                  }
                  if (printCount > PRINT_WARNING_THRESHOLD) {
                    const ok = window.confirm(
                      `現在の条件では ${printCount} 件を印刷しようとしています。本当に印刷してよいですか？`
                    );
                    if (!ok) return;
                  }
                  window.print();
                }}
                className="btn-toolbar bg-slate-700 text-white rounded-md text-sm hover:bg-slate-800"
              >
                印刷実行
              </button>
            </div>
          </div>
        </div>
        <div className={PRINT_PAGE_TITLE_ROW}>
          <h1 className={PRINT_PAGE_TITLE_H1}>入荷予定一覧</h1>
          <span className={PRINT_PAGE_TITLE_COUNT}>件数: {printCount}</span>
        </div>
        <div className={PRINT_SCROLL_CONTAINER}>
          <table className={PRINT_TABLE}>
            <colgroup>
              {INBOUND_PRINT_COL_WIDTHS.map((w, i) => (
                <col key={i} style={{ width: w }} />
              ))}
            </colgroup>
            <thead className={PRINT_THEAD}>
              <tr className={PRINT_SEARCH_ROW_TR}>
                <th colSpan={8} className={PRINT_SEARCH_ROW_TH}>
                  {getPrintSearchSummary()}
                </th>
              </tr>
              <tr>
                <th className={PRINT_TH_LEFT}>予定納期</th>
                <th className={PRINT_TH_LEFT}>発注No</th>
                <th className={PRINT_TH_LEFT}>発注日</th>
                <th className={PRINT_TH_LEFT}>仕入先</th>
                <th className={PRINT_TH_LEFT}>製品コード</th>
                <th className={PRINT_TH_LEFT}>製品名/寸法</th>
                <th className={PRINT_TH_RIGHT}>数量</th>
                <th className={PRINT_TH_LEFT}>単位</th>
              </tr>
            </thead>
            <tbody>
              {printCount === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-6 text-center text-slate-500 text-sm">
                    データがありません
                  </td>
                </tr>
              ) : (
                printSource.slice(0, printCount).map((row, idx) => (
                  <tr
                    key={`${row.hacchu_code}-${row.hacchu_changecnt}-${row.hacchu_datacode}-${idx}`}
                    className={PRINT_TR_BODY}
                  >
                    <td className={PRINT_TD_LEFT}>{formatDate(row.supplier_response_due_date)}</td>
                    <td className={PRINT_TD_LEFT}>{row.hacchu_no || '-'}</td>
                    <td className={PRINT_TD_LEFT}>{formatDate(row.hacchu_date)}</td>
                    <td className={PRINT_TD_LEFT}>{row.supplier_name || '-'}</td>
                    <td className={PRINT_TD_LEFT}>{row.product_disp || '-'}</td>
                    <td className={PRINT_TD_COMPOSITE}>
                      <div className="whitespace-normal break-words leading-snug">
                        <div>{row.product_name || '-'}</div>
                        <div className="text-slate-600">{row.product_size || '-'}</div>
                      </div>
                    </td>
                    <td className={PRINT_TD_NUM}>{formatNumber(row.hacchu_amount)}</td>
                    <td className={PRINT_TD_LEFT}>{row.tani || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (isPrintView) {
    return renderPrintView();
  }

  return (
    <div className="h-full flex flex-col bg-slate-50">
      <div className="page-section">
        <div className="page-header">
          <h1 className="page-title">入荷予定</h1>
          <div className="page-header-actions">
            <PageSyncMeta groups={['orders']} />
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="flex items-center gap-2 btn-toolbar bg-slate-700 text-white rounded-md hover:bg-slate-800 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg
                className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              更新
            </button>
            <button
              type="button"
              onClick={() => {
                const params = new URLSearchParams(searchParams);
                params.set('print_view', '1');
                const url = `${window.location.pathname}?${params.toString()}`;
                window.open(url, '_blank');
              }}
              className="flex items-center gap-2 btn-toolbar bg-slate-100 text-slate-700 rounded-md hover:bg-slate-200 transition-colors font-medium text-sm border border-slate-300"
            >
              印刷用表示
            </button>
          </div>
        </div>
      </div>

      {/* タブ */}
      <div className="bg-white border-b border-slate-200 page-section-x">
        <div className="flex space-x-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-slate-900 border-b-2 border-slate-900'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* 一覧表示 */}
      <div className="flex-1 overflow-hidden flex">
        <div className="flex-1 flex flex-col page-section-x pt-4 pb-2 min-w-0 min-h-0">
          <div className="mb-2 bg-white rounded-lg shadow-sm border border-slate-200 h-full flex flex-col">
            <div className="flex-1 overflow-auto">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <svg className="animate-spin h-8 w-8 text-slate-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              ) : items.length === 0 ? (
                <div className="px-4 py-8 text-center text-slate-500">
                  データがありません
                </div>
              ) : (
                <table className="divide-y divide-slate-200" style={{ minWidth: 'max-content', width: '100%' }}>
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider bg-slate-50 sticky top-0 z-10">
                        予定納期
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider bg-slate-50 sticky top-0 z-10">
                        発注No
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider bg-slate-50 sticky top-0 z-10">
                        発注日
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider bg-slate-50 sticky top-0 z-10">
                        仕入先
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider bg-slate-50 sticky top-0 z-10">
                        製品コード
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider bg-slate-50 sticky top-0 z-10">
                        製品名
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider bg-slate-50 sticky top-0 z-10">
                        寸法
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider bg-slate-50 sticky top-0 z-10">
                        数量
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider bg-slate-50 sticky top-0 z-10">
                        単位
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider bg-slate-50 sticky top-0 z-10">
                        分納計画
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-100">
                    {items.map((row, rowIdx) => (
                      <tr key={`${row.hacchu_code}-${row.hacchu_changecnt}-${row.hacchu_datacode}-${row.is_split_plan ? row.split_no : 'detail'}`} className="hover:bg-slate-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-900">
                          {formatDate(row.supplier_response_due_date)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-900">
                          {row.hacchu_no}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-900">
                          {formatDate(row.hacchu_date)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-900">
                          {row.supplier_name || '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-900">
                          {row.product_disp || '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-900">
                          {row.product_name || '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-900">
                          {row.product_size || '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-900">
                          {row.is_split_plan ? formatNumber(row.planned_qty) : formatNumber(row.hacchu_amount)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-900">
                          {row.tani || '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-900">
                          {row.is_split_plan ? `分納${row.split_no} (${formatNumber(row.planned_qty)}${row.tani || ''})` : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InboundSchedulePage;
